import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import DOMPurify from "dompurify";
import { Rating } from "primereact/rating";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill/lib";
import ConfirmDocument from "../../components/ConfirmDocument/ConfirmDocument";
import Navbar from "../../components/Navbar/Navbar";
import ReviewsModal from "../../components/ReviewsModal/ReviewsModal";
import { useToastContext } from "../../contexts/ToastContext/ToastContext";
import useGetCommentsOfStore from "../../hooks/useGetCommentsOfStore";
import { useToggle } from "../../hooks/useToggle";
import localeText from "../../locale";
import GetUserInfoService from "../../service/GetUserInfoService";
import { cloudinaryInstance } from "../../utilities/Cloudinary/cloudinaryInstance";
import { parseProduct } from "../../utilities/parsers/product";
import ProductCard from "../ProductInfo/components/ProductCard/ProductCard";
import LandingFooter from "../Landing/components/LandingFooter/LandingFooter";
import LandingCopyright from "../Landing/components/LandingCopyright/LandingCopyright";
import useLoadingSimulation from "../Shop/hooks/useLoadingSimulation";
import { Skeleton } from "primereact/skeleton";
import ProductSectionsLoading from "../Shop/components/ProductSectionsLoading/ProductSectionsLoading";
import StorePageIcon from "./StorePageIcon";
import UserStoreHeader from "./Components/UserStoreHeader";

const ProductInfo = (props) => {
  const [loading, setLoading] = useState(false);
  const [userRating, setUserRating] = useState(null);
  const [storeInfo, setStoreInfo] = useState(null);

  let user = props.user;
  let slug = props.slug;

  const toast = useRef(null);

  const getUserInfoService = new GetUserInfoService();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancel = false;

    async function fetchData() {
      try {
        setLoading(true);

        let res = null;

        if (user) {
          res = await getUserInfoService.getUserShop(user);
        }
        if (slug) {
          res = await getUserInfoService.getShopBySlug(slug);
          user = res.result.shopConfiguration?.user?.username;
        }
        if (cancel) return;

        if (res.messageCode !== 200001) {
          toast.current.show({
            severity: "error",
            summary: localeText("error"),
            detail: localeText("data").find((msg) => msg.code === res.messageCode).message,
            life: 6000,
          });
          if (res.messageCode === 500000) console.log(res);
          setLoading(false);
        } else {
          setStoreInfo({ ...res.result, products: res.result.products.map(parseProduct) });

          const resRating = await getUserInfoService.getUserRating(user);

          if (resRating.messageCode !== 200001) {
            toast.current.show({
              severity: "error",
              summary: localeText("error"),
              detail: localeText("data").find((msg) => msg.code === resRating.messageCode).message,
              life: 6000,
            });
            if (resRating.messageCode === 500000) console.log(resRating);
            setLoading(false);
          } else {
            setUserRating(resRating.result);
            setLoading(false);
          }
        }
      } catch (e) {
        setLoading(false);
        console.log(e);
      }
    }

    fetchData();

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { showErrorToast } = useToastContext();
  const { value, setAsFalse, setAsTrue } = useToggle();
  const { isLoading, commentsOfStore } = useGetCommentsOfStore({
    onError: ({ code }) => showErrorToast({ code }),
    username: user,
  });
  const isLoadingSimulation = useLoadingSimulation();

  console.log(storeInfo);

  return (
    <>
      <ReviewsModal
        headerText={`${localeText("storeReviews")} (${commentsOfStore.length})`}
        setAsFalse={setAsFalse}
        visible={value}
        comments={commentsOfStore}
        isLoading={isLoading}
      />
      {/*  <Navbar /> */}
      <Toast ref={toast} />

      <ConfirmDocument />

      <div className="content-grid bg-white pt-6 md:pt-8">
        {loading ? (
          <div className="overflow-hidden">
            <Skeleton width="30rem" height="2rem" className="mb-3" />
            <Skeleton width="50rem" height="1rem" className="mb-6" />
            <ProductSectionsLoading />
          </div>
        ) : (
          <>
            {storeInfo && (
              <>
                {!storeInfo.shopConfiguration ? (
                  <div className="text-center my-8">
                    <i className="pi pi-shopping-bag text-8xl text-400"></i>
                    <h4 className="text-500">{localeText("thisUserHasntSetUpStore")}</h4>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-nogutter gap-5 justify-content-center">
                      <div
                        className="border-round-lg p-2 flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #00000014",
                          width: "fit-content",
                          maxWidth: "100%",
                        }}
                      >
                        <AdvancedImage
                          cldImg={cloudinaryInstance
                            .image(storeInfo.shopConfiguration.imagelogoid)
                            .resize(fill().width(480).height(120))
                            .format("webp")}
                          className="border-round-lg"
                          style={{
                            display: "block",
                            width: "100%",
                            maxWidth: "480px",
                            height: "auto",
                            aspectRatio: "4/1"
                          }}
                        />
                      </div>
                      {storeInfo.shopConfiguration.video && (
                        <div className="col-12 flex justify-content-center">
                          <video
                            controls
                            className="border-round-lg w-full"
                            style={{
                              maxWidth: "800px",
                              maxHeight: "450px",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                            }}
                            src={storeInfo.shopConfiguration.video}
                          >
                            {localeText("videoNotSupported")}
                          </video>
                        </div>
                      )}
                      <UserStoreHeader storeInfo={storeInfo} />
                    </div>
                    <hr className="mt-4 mb-0" />
                    <div className="mt-5 mb-8">
                      <div className="shop-products-section w-full">
                        {storeInfo.products.map((product) => {
                          return (
                            <div key={`store${product.id}`}>
                              <ProductCard product={product} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
        {/* <Footer /> */}
        <LandingFooter />
        <LandingCopyright />
        <section
          className="md:py-5 full-width"
          style={{ backgroundColor: "#FFFFFF", color: "#7A869A", borderTop: "1px solid #E0E7FF" }}
        >
          <div className="grid gap-3 m-0">
            <div className="col-12 md:col text-center md:text-left">
              <div className="text-sm font-regular">EXTRACONOMY GLOBAL srl</div>
              <div className="text-sm font-regular">Via Nomentana, 1018 - 00137 Roma (IT)</div>
            </div>
            <div className="col-12 md:col text-center md:text-left">
              <div className="text-sm font-regular">P. Iva e C.F. 16948681008 | REA 1686351</div>
            </div>
            <div className="col-12 md:col text-center md:text-right">
              <div className="text-sm font-regular">Privacy Policy</div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductInfo;
